import { useCallback, useRef } from "react";

/**
 * Bars the toasts must not cover: the Orders cart bar (phone and portrait
 * tablet), the cart's action footer (Bill, KOT, Settle) and the Tables merge bar.
 * A bar registers with `ref={useToastAnchor()}`; the toast stack then keeps
 * clear of every registered bar it would otherwise cover (toastPlacement).
 */
const anchors = new Set<HTMLElement>();
const listeners = new Set<() => void>();

function changed() {
  for (const listener of listeners) listener();
}

export function getToastAnchors(): ReadonlySet<HTMLElement> {
  return anchors;
}

export function subscribeToastAnchors(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A ref for a bottom bar the toasts must not cover. */
export function useToastAnchor<T extends HTMLElement>(): (element: T | null) => void {
  const current = useRef<T | null>(null);
  return useCallback((element: T | null) => {
    if (current.current === element) return;
    if (current.current) anchors.delete(current.current);
    current.current = element;
    if (element) anchors.add(element);
    changed();
  }, []);
}

export interface ToastPlacementOptions {
  /** The column's widest. */
  maxWidth: number;
  /** Space kept each side of the column. */
  gutter: number;
  /** The column moves beside a bar only if it can stay at least this wide there. */
  minWidth: number;
}

export interface ToastPlacement {
  /** Px from the bottom of the screen to the bottom of the stack area (0: the edge). */
  bottom: number;
  /** Px kept free on the right: the column centres in the space left of it. */
  right: number;
}

/** The toast column: centred in the first `areaWidth` px, at most `maxWidth` wide, with a gutter each side. */
export function toastColumn(areaWidth: number, maxWidth: number, gutter: number): { left: number; right: number } {
  const width = Math.max(0, Math.min(maxWidth, areaWidth - gutter * 2));
  const left = (areaWidth - width) / 2;
  return { left, right: left + width };
}

const onScreen = (box: DOMRect, viewportHeight: number) =>
  box.width > 0 && box.height > 0 && box.top < viewportHeight && box.bottom > 0;
const sharesColumn = (box: DOMRect, column: { left: number; right: number }) => box.right > column.left && box.left < column.right;

/**
 * Where the stack goes, given the registered bars on screen:
 * - a bar docked on the right half (the cart on a landscape tablet) that the
 *   centred column would cover moves the column left, centred in the space
 *   beside it, when it fits there at `minWidth`;
 * - any bar still in the column (the phone cart bar, the cart sheet's actions,
 *   the merge bar) lifts the stack above its top.
 */
export function toastPlacement(viewportWidth: number, viewportHeight: number, options: ToastPlacementOptions): ToastPlacement {
  const boxes = [...anchors].map((anchor) => anchor.getBoundingClientRect()).filter((box) => onScreen(box, viewportHeight));
  const centred = toastColumn(viewportWidth, options.maxWidth, options.gutter);
  let right = 0;
  for (const box of boxes) {
    if (!sharesColumn(box, centred) || box.left < viewportWidth / 2) continue;
    if (box.left - options.gutter * 2 < options.minWidth) continue;
    right = Math.max(right, viewportWidth - box.left);
  }
  const column = toastColumn(viewportWidth - right, options.maxWidth, options.gutter);
  let bottom = 0;
  for (const box of boxes) {
    if (sharesColumn(box, column)) bottom = Math.max(bottom, viewportHeight - box.top);
  }
  return { bottom, right };
}
